import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QuickFireIdeaGenerator } from '../../components/journey/QuickFireIdeaGenerator';
import { TabbedIdeaSelector } from '../../components/journey/TabbedIdeaSelector';
import { BusinessIdea } from '../../types/ai';
import BasePageLayout from '../../components/BasePageLayout';
import { JourneyPlatformIntegrationService } from '../../services/journeyPlatformIntegrationService';
import { useLanguage } from '../../context/LanguageContext';
import './AIIdeaGeneratorPage.css';

export const AIIdeaGeneratorPage: React.FC = () => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState<'generate' | 'select'>('generate');
  const [generatedIdeas, setGeneratedIdeas] = useState<BusinessIdea[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<BusinessIdea | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get journey context from URL params
  const stepId = searchParams.get('stepId');
  const journeyId = searchParams.get('journeyId');
  const actionType = searchParams.get('actionType');

  useEffect(() => {
    // If we have journey context, we're being called from a workflow step
    if (stepId && journeyId && actionType) {
      console.log('AI Idea Generator called from journey step:', { stepId, journeyId, actionType });
    }
  }, [stepId, journeyId, actionType]);

  const handleIdeasGenerated = (ideas: BusinessIdea[]) => {
    setGeneratedIdeas(ideas);
    setCurrentStep('select');
    setError(null);

    // Record the completion of the AI idea generation action
    if (stepId && actionType) {
      JourneyPlatformIntegrationService.trackActionCompletion(
        stepId,
        actionType,
        'ai-ideas-generated'
      );
    }
  };

  const handleIdeaSelected = async (idea: BusinessIdea) => {
    setSelectedIdea(idea);
    setIsLoading(true);

    try {
      // Store the selected idea in sessionStorage for retrieval by the journey
      if (journeyId) {
        const selectedIdeaKey = `selected_idea_${journeyId}`;
        sessionStorage.setItem(selectedIdeaKey, JSON.stringify(idea));
        console.log('💾 Stored selected idea in sessionStorage:', idea.title);
        console.log('🔑 Storage key:', selectedIdeaKey);
        console.log('📄 Stored data:', JSON.stringify(idea));
      }

      // Track action completion for analytics
      if (stepId && actionType) {
        JourneyPlatformIntegrationService.trackActionCompletion(
          stepId,
          actionType,
          'ai-idea-selected'
        );
      }

      // Navigate back to the journey workflow
      if (journeyId) {
        console.log('🧭 Navigating to:', `/journey/${journeyId}?ideaSelected=true`);
        navigate(`/journey/${journeyId}?ideaSelected=true`);
      } else {
        // If not called from journey, go to general journey page
        console.log('🧭 Navigating to general journey page');
        navigate('/journey');
      }
    } catch (error) {
      console.error('Error recording idea selection:', error);
      setError('Failed to save your idea selection. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleBackToGenerate = () => {
    setCurrentStep('generate');
    setGeneratedIdeas([]);
    setError(null);
  };

  return (
    <BasePageLayout className="ai-idea-generator-page" maxWidth="none">
      <div className="page-container p-md">
        {/* Page header - Only show during generate step */}
        {currentStep === 'generate' && (
          <div className="page-header">
            <div className="header-content">
              <h1 className="page-title">{t('aiIdeaGenerator.pageTitle')}</h1>
              <p className="page-subtitle">{t('aiIdeaGenerator.generatorSubtitle')}</p>
            </div>
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="error-banner" role="alert">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{error}</span>
            <button 
              className="error-dismiss"
              onClick={() => setError(null)}
              aria-label={t('aiIdeaGenerator.errorDismiss')}
            >
              ×
            </button>
          </div>
        )}

        {/* Main content */}
        <div className="main-content">
          {currentStep === 'generate' && (
            <div className="generate-step">
              <QuickFireIdeaGenerator
                onIdeasGenerated={handleIdeasGenerated}
                onError={handleError}
                currentStep={currentStep}
                showStepsIndicator={false}
              />
            </div>
          )}

          {currentStep === 'select' && generatedIdeas.length > 0 && (
            <div className="select-step">
              <div className="select-header">
                <h2>{t('aiIdeaGenerator.chooseYourIdea')}</h2>
                <p>{t('aiIdeaGenerator.reviewAndSelect')}</p>
              </div>
              
              <TabbedIdeaSelector
                ideas={generatedIdeas}
                onIdeaSelected={handleIdeaSelected}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>

        {/* Navigation - Only show back button when in select step */}
        {currentStep === 'select' && (
          <div className="page-navigation">
            <button
              className="btn-secondary nav-back"
              onClick={handleBackToGenerate}
              disabled={isLoading}
            >
              {t('aiIdeaGenerator.backToGenerator')}
            </button>
          </div>
        )}
      </div>
      
    </BasePageLayout>
  );
};

export default AIIdeaGeneratorPage;
